import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
    Ticket,
    TicketDateienListElement,
    TicketFarben,
    TicketKommentar,
    TicketPrioritaet,
    TicketZuweisung,
} from '../../../../projects/kiene-core/src/lib/classes/ticket';
import { ViewImageDialogComponent } from '../../../../projects/kiene-core/src/lib/components/create-ticket-dialog/create-ticket-view-image-dialog.component';
import { ConfirmDialogInput } from '../../../../projects/kiene-core/src/lib/components/dialogs/confirm-dialog/confirm-dialog';
import { ConfirmDialogComponent } from '../../../../projects/kiene-core/src/lib/components/dialogs/confirm-dialog/confirm-dialog.component';
import { EnterValueDialogComponent } from '../../../../projects/kiene-core/src/lib/components/dialogs/enter-value-dialog/enter-value-dialog.component';
import { EnterValueInput } from '../../../../projects/kiene-core/src/lib/components/dialogs/enter-value-dialog/enter-value-response';
import { PdfViewerInput } from '../../../../projects/kiene-core/src/lib/components/pdf-viewer/kiene-pdf-viewer/classes/pdf-viewer-input';
import { PdfViewerDialogComponent } from '../../../../projects/kiene-core/src/lib/components/pdf-viewer/pdf-viewer-dialog/pdf-viewer-dialog.component';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneBackendApiService } from '../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { LocalStorageService } from '../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { PrioritaetenDialogComponent } from '../prioritaeten-dialog/prioritaeten-dialog.component';
import { User } from '../../../../projects/kiene-core/src/lib/components/stammdaten/benutzer/benutzer';
import { TicketTypeDialogComponent } from './../ticket-type-dialog/ticket-type-dialog.component';

@Component({
    selector: 'kiene-ticket-details',
    templateUrl: './ticket-details.component.html',
    styleUrls: ['./ticket-details.component.scss'],
})
export class TicketDetailsComponent implements OnInit {
    @ViewChild('tabGroup') tabGroup: MatTabGroup;

    neuerKommentarPublicCtrl = new FormControl(true);
    neuerKommentarCtrl = new FormControl();
    currentUser = this.localStorageService.getCurrentUser();
    isAdmin = false;
    isSupportUser = false;

    ticket: Ticket = new Ticket();
    selectedFiles: File[] = [];
    selectedIndex = 0;
    canNotEdit = true;
    prioritaeten: TicketPrioritaet[] = [];

    loadTicketSub = new Subscription();
    downloadSub = new Subscription();
    isLoadingOrSaving: boolean;
    userInZugewiesenAndStatusAbFreigegeben = false;

    constructor(
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly route: ActivatedRoute,
        private readonly localStorageService: LocalStorageService,
        private readonly api: KieneBackendApiService,
        private readonly messageService: MessageService,
        private readonly dialog: MatDialog,
        private readonly router: Router,
    ) {}

    ngOnInit(): void {
        const ticket_id = +this.route.snapshot.paramMap.get('id');
        this.isAdmin = this.localStorageService.currentUserIsAdmin();
        if (ticket_id) {
            this.loadTicket(ticket_id);
        } else {
            this.messageService.alertMessage('Ticket ID nicht übergeben!');
        }

        this.isSupportUser = this.localStorageService.isCurrentUserSupport();
        this.loadPrioritaeten();
    }

    public addComment(): void {
        const neuerKommentar: string = this.neuerKommentarCtrl.value;
        if (!neuerKommentar || this.isLoadingOrSaving) {
            return;
        }

        const kommentar = new TicketKommentar();
        kommentar.kommentar = neuerKommentar;
        kommentar.ticket_id = this.ticket.ticket_id;
        kommentar.erstellt_von = this.currentUser?.benutzer_id;
        kommentar.oeffentlich = this.isAdmin ? (this.neuerKommentarPublicCtrl.value ? 1 : 0) : 1;
        this.postComment(kommentar);
    }

    public setStatus(status_id: number): void {
        this.ticket.status_id = status_id;
        this.updateTicket();
    }

    public setPrioritaet(): void {
        this.isLoadingOrSaving = true;
        this.dialog
            .open(PrioritaetenDialogComponent, { data: this.ticket })
            .afterClosed()
            .subscribe({
                next: () => {
                    this.loadTicket(this.ticket.ticket_id);
                    this.isLoadingOrSaving = false;
                },
            });
    }

    public getPrioritaetTextColor(prio?: number): { text: string; color: string } {
        switch (Number(prio ?? this.ticket.prioritaet)) {
            case 10:
                return { text: 'Niedrig', color: '#f5c652' };
            case 20:
                return { text: 'Mittel', color: '#d96f1b' };
            case 30:
                return { text: 'Hoch', color: '#ee0909' };
            case 40:
                return { text: 'Dringend', color: '#700202' };
            default:
                return { text: '', color: '' };
        }
    }

    public deleteFile(file: TicketDateienListElement): void {
        const confirmInput = new ConfirmDialogInput();
        confirmInput.headline = 'Datei löschen';
        confirmInput.text = 'Soll die Datei wirklich gelöscht werden?';
        confirmInput.okButton = 'Löschen';

        this.dialog
            .open(ConfirmDialogComponent, { data: confirmInput })
            .afterClosed()
            .subscribe({
                next: result => {
                    if (result?.ok && result?.confirmed) {
                        this.isLoadingOrSaving = true;
                        this.api.deleteEntity(this.apiBaseUrl + 'ticket_dokument/delete.php', file).subscribe({
                            next: () => {
                                this.isLoadingOrSaving = false;
                                this.reloadTicket();
                                this.messageService.infoMessage('Datei wurde gelöscht');
                            },
                            error: err => {
                                this.isLoadingOrSaving = false;
                                this.messageService.errorMessage('Datei konnte nicht gelöscht werden!', err);
                            },
                        });
                    }
                },
            });
    }

    public onFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) return;

        const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf', 'csv', 'xls', 'xlsx'];
        const validFiles: File[] = [];

        for (let i = 0; i < input.files.length; i++) {
            const file = input.files[i];
            const fileExtension = file.name.split('.').pop()?.toLowerCase();
            if (fileExtension && allowedExtensions.includes(fileExtension)) {
                validFiles.push(file);
            } else {
                alert(`Die Datei \`${file.name}\` hat eine unerlaubte Dateiendung und wurde nicht hinzugefügt.`);
            }
        }

        this.selectedFiles = [...this.selectedFiles, ...validFiles];
        if (this.selectedFiles.length > 0) {
            this.uploadFiles();
        }
    }

    public updateBeschreibung(): void {
        this.updateTicket();
    }

    public canDeleteComment(comment: TicketKommentar): boolean {
        if (this.localStorageService.currentUserIsAdmin()) {
            return true;
        }
        if (!this.localStorageService.hasPermission(10038)) {
            return false;
        }
        return comment.erstellt_von === this.currentUser?.benutzer_id;
    }

    public canEditComment(comment: TicketKommentar): boolean {
        if (!this.localStorageService.hasPermission(10037)) {
            return false;
        }
        return comment.erstellt_von === this.currentUser?.benutzer_id;
    }

    public canDeleteFile(datei: TicketDateienListElement): boolean {
        if (this.localStorageService.currentUserIsAdmin()) {
            return true;
        }
        if (!this.localStorageService.hasPermission(10038)) {
            return false;
        }
        return datei.erstellt_von === this.currentUser?.benutzer_id;
    }

    public editComment(comment: TicketKommentar): void {
        const input = new EnterValueInput();
        input.title = 'Kommentar bearbeiten';
        input.okButton = 'Speichern';
        input.addInputValue('Kommentar bearbeiten', 'text', comment.kommentar);
        if (this.isAdmin) {
            input.addInputValue('Öffentlich', 'toggle', !!comment.oeffentlich);
        }

        this.dialog
            .open(EnterValueDialogComponent, { data: input })
            .afterClosed()
            .subscribe({
                next: result => {
                    if (!result?.result) return;
                    comment.kommentar = result.values[0];
                    comment.oeffentlich = this.isAdmin ? (result.values[1] ? 1 : 0) : 1;
                    this.isLoadingOrSaving = true;
                    this.api.post(this.apiBaseUrl + 'ticket_kommentar/update.php', comment).subscribe({
                        next: () => {
                            this.isLoadingOrSaving = false;
                            this.messageService.infoMessage('Kommentar wurde geändert');
                            this.loadTicket(this.ticket.ticket_id);
                        },
                        error: err => {
                            this.isLoadingOrSaving = false;
                            this.messageService.errorMessage('Kommentar konnte nicht geändert werden!', err);
                        },
                    });
                },
            });
    }

    public deleteComment(kommentar: TicketKommentar): void {
        const input = new ConfirmDialogInput();
        input.headline = 'Kommentar löschen';
        input.text = 'Soll der Kommentar wirklich gelöscht werden?';
        input.okButton = 'Löschen';

        this.dialog
            .open(ConfirmDialogComponent, { data: input })
            .afterClosed()
            .subscribe({
                next: result => {
                    if (result?.confirmed) {
                        this.isLoadingOrSaving = true;
                        this.api.deleteEntity(this.apiBaseUrl + 'ticket_kommentar/delete.php', kommentar).subscribe({
                            next: () => {
                                this.ticket.kommentare = this.ticket.kommentare.filter(
                                    k => k.tk_id !== kommentar.tk_id,
                                );
                                this.isLoadingOrSaving = false;
                                this.reloadTicket();
                            },
                            error: err => {
                                this.isLoadingOrSaving = false;
                                this.messageService.errorMessage('Kommentar konnte nicht gelöscht werden!', err);
                            },
                        });
                    }
                },
            });
    }

    public openOrDownloadFile(file: TicketDateienListElement): void {
        this.downloadSub?.unsubscribe();
        const path = this.getDownloadPath(file);
        const params = new HttpParams().set('td_id', file.td_id.toString());
        const isBlob =
            file.mimetype.includes('application/pdf') ||
            file.mimetype.includes('text/csv') ||
            file.mimetype.includes('application/vnd');

        this.isLoadingOrSaving = true;
        if (isBlob) {
            this.downloadSub = this.api.getBlob(path, params).subscribe({
                next: res => {
                    this.handleFileResponse(res, file);
                },
                error: err => {
                    this.handleDownloadError(err, path);
                },
            });
        } else {
            this.downloadSub = this.api.get(path, params).subscribe({
                next: res => {
                    this.handleFileResponse(res, file);
                },
                error: err => {
                    this.handleDownloadError(err, path);
                },
            });
        }
    }

    public goBack() {
        this.router.navigate(['..'], { relativeTo: this.route });
    }

    public getColor(status_id: number): string {
        return TicketFarben.getColor(status_id);
    }

    public addBenutzer(user: User): void {
        if (!user || this.isAlreadyAssigned(user) || this.isLoadingOrSaving) return;

        const zuweisung = new TicketZuweisung();
        zuweisung.ticket_id = this.ticket.ticket_id;
        zuweisung.benutzer_id = user.benutzer_id;
        zuweisung.erstellt_von = this.currentUser?.benutzer_id;
        zuweisung.erstellt = new Date().toISOString();
        zuweisung.vorname = user.vorname;
        zuweisung.nachname = user.nachname;

        this.isLoadingOrSaving = true;
        this.api.post(this.apiBaseUrl + 'ticket_zuweisung/create.php', zuweisung).subscribe({
            next: res => {
                if (!this.ticket.zuweisungen) this.ticket.zuweisungen = [];
                if (res) zuweisung.tz_id = res.tz_id;
                this.ticket.zuweisungen.push(zuweisung);
                this.isLoadingOrSaving = false;
                this.messageService.infoMessage('Benutzer wurde zugewiesen');
            },
            error: err => {
                this.isLoadingOrSaving = false;
                this.messageService.errorMessage('Benutzer konnte nicht zugewiesen werden!', err);
            },
        });
    }

    public deleteBenutzer(tz: TicketZuweisung): void {
        const input = new ConfirmDialogInput();
        input.headline = 'Zuweisung entfernen';
        input.text = 'Soll der Benutzer wirklich aus den Zuweisungen entfernt werden?';
        input.okButton = 'Entfernen';

        this.dialog
            .open(ConfirmDialogComponent, { data: input })
            .afterClosed()
            .subscribe({
                next: result => {
                    if (result?.ok && result?.confirmed) {
                        this.isLoadingOrSaving = true;
                        this.api.deleteEntity(this.apiBaseUrl + 'ticket_zuweisung/delete.php', tz).subscribe({
                            next: () => {
                                this.ticket.zuweisungen = this.ticket.zuweisungen.filter(z => z.tz_id !== tz.tz_id);
                                this.isLoadingOrSaving = false;
                                this.messageService.infoMessage('Benutzer wurde erfolgreich gelöscht');
                            },
                            error: err => {
                                this.isLoadingOrSaving = false;
                                this.messageService.errorMessage('Benutzer konnte nicht gelöscht werden!', err);
                            },
                        });
                    }
                },
            });
    }

    public tabChanged(event: MatTabChangeEvent): void {
        this.selectedIndex = event.index;
    }

    public getKundenInfos(ticket: Ticket): any {
        return ticket.kunde;
    }

    setType() {
        this.isLoadingOrSaving = true;
        this.dialog
            .open(TicketTypeDialogComponent, { data: this.ticket })
            .afterClosed()
            .subscribe({
                next: () => {
                    this.loadTicket(this.ticket.ticket_id);
                    this.isLoadingOrSaving = false;
                },
            });
    }

    private loadTicket(ticket_id: number): void {
        this.loadTicketSub?.unsubscribe();
        this.isLoadingOrSaving = true;
        const params = new HttpParams().set('ticket_id', ticket_id);
        this.loadTicketSub = this.api.get(this.apiBaseUrl + 'ticket/read_one.php', params).subscribe({
            next: res => {
                this.ticket = res;
                this.userCanNotEdit();
                this.isUserInZugewiesenAndStatusAbFreigegeben();
                this.loadTicketSub.unsubscribe();
                this.isLoadingOrSaving = false;
            },
            error: err => {
                this.loadTicketSub.unsubscribe();
                this.isLoadingOrSaving = false;
                this.messageService.errorMessage('Fehler beim Laden des Tickets', err);
            },
        });
    }

    private loadPrioritaeten(): void {
        this.isLoadingOrSaving = true;
        this.api.getAll(this.apiBaseUrl + 'ticket_prioritaet/read.php').subscribe({
            next: res => {
                this.prioritaeten = res.records;
                this.isLoadingOrSaving = false;
            },
            error: err => {
                this.isLoadingOrSaving = false;
                this.messageService.errorMessage('Fehler beim Laden der Prioritäten', err);
            },
        });
    }

    private uploadFiles(): void {
        this.ticket.files = this.selectedFiles;

        const formData = new FormData();
        formData.set('ticket_id', this.ticket.ticket_id.toString());
        formData.set('editiert_von', this.currentUser.benutzer_id.toString());

        this.selectedFiles.forEach(file => formData.append('dokument[]', file));

        const options = {
            headers: new HttpHeaders('Phito-Type: fileupload'),
            params: new HttpParams(),
            reportProgress: true,
        };

        this.isLoadingOrSaving = true;
        this.api.postFormData(this.apiBaseUrl + 'ticket/update.php', formData, options).subscribe({
            next: () => {
                this.messageService.infoMessage('Ticket wurde gespeichert');
                this.selectedFiles = [];
                this.reloadTicket();
                this.isLoadingOrSaving = false;
            },
            error: error => {
                this.messageService.errorMessage('Ticket konnte nicht erstellt werden!', error);
                this.isLoadingOrSaving = false;
            },
        });
    }

    private reloadTicket(): void {
        this.loadTicket(this.ticket.ticket_id);
    }

    private postComment(kommentar: TicketKommentar): void {
        this.isLoadingOrSaving = true;
        this.api.post(this.apiBaseUrl + 'ticket_kommentar/create.php', kommentar).subscribe({
            next: () => {
                if (!this.ticket.kommentare) this.ticket.kommentare = [];
                this.ticket.kommentare.push(kommentar);
                this.neuerKommentarCtrl.reset();
                this.reloadTicket();
                this.isLoadingOrSaving = false;
            },
            error: err => {
                this.messageService.errorMessage('Kommentar konnte nicht gespeichert werden!', err);
                this.isLoadingOrSaving = false;
            },
        });
    }

    private updateTicket(): void {
        this.isLoadingOrSaving = true;
        this.api.post(this.apiBaseUrl + 'ticket/update.php', this.ticket).subscribe({
            next: () => {
                this.messageService.infoMessage('Änderungen am Ticket wurde gespeichert');
                this.isLoadingOrSaving = false;
                this.reloadTicket();
            },
            error: err => {
                this.messageService.errorMessage('Ticket konnte nicht gespeichert werden!', err);
                this.isLoadingOrSaving = false;
            },
        });
    }

    private handleDownloadError(err: any, path: string): void {
        console.error('Download error:', err, path);
        this.downloadSub?.unsubscribe();
        this.isLoadingOrSaving = false;
        this.messageService.errorMessage('Datei konnte nicht geöffnet werden!', err.error);
    }

    private getDownloadPath(file: TicketDateienListElement): string {
        const basePath = `${this.apiBaseUrl}ticket_dokument/`;
        if (file.mimetype.startsWith('image')) {
            return `${basePath}read_image.php?td_id=${file.td_id}`;
        }
        if (file.mimetype.startsWith('application/pdf')) {
            return `${basePath}read_pdf.php`;
        }
        return `${basePath}read_download.php?td_id=${file.td_id}`;
    }

    private openImageDialog(imageSrc: string, titel: string): void {
        this.isLoadingOrSaving = true;
        this.dialog
            .open(ViewImageDialogComponent, { data: { imageSrc: imageSrc, titel: titel } })
            .afterClosed()
            .subscribe({ next: () => (this.isLoadingOrSaving = false) });
    }

    private openPdfDialog(file: TicketDateienListElement): void {
        const pdfViewerInput = new PdfViewerInput();
        pdfViewerInput.url = `ticket_dokument/read_pdf.php?td_id=${file.td_id}`;
        pdfViewerInput.betreff = 'Ticket Dokument';
        pdfViewerInput.dateiname = file.dateiname;

        this.isLoadingOrSaving = false;
        this.dialog
            .open(PdfViewerDialogComponent, { data: pdfViewerInput, width: '80%' })
            .afterClosed()
            .subscribe({ next: () => (this.isLoadingOrSaving = false) });
    }

    private handleFileResponse(res: any, file: TicketDateienListElement): void {
        this.downloadSub?.unsubscribe();
        this.isLoadingOrSaving = false;

        if (file.mimetype.startsWith('image')) {
            this.openImageDialog(res.base64Image as string, file.dateiname);
            return;
        }
        if (file.mimetype.includes('application/pdf')) {
            const reader = new FileReader();
            reader.onload = () => this.openPdfDialog(file);
            reader.readAsDataURL(res);
            return;
        }
        const a = document.createElement('a');
        const url = window.URL.createObjectURL(res);
        a.href = url;
        a.download = file.dateiname;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
    }

    private isAlreadyAssigned(user: User): boolean {
        return !!this.ticket.zuweisungen?.find(z => z.benutzer_id === user.benutzer_id);
    }

    private userCanNotEdit(): void {
        if (this.localStorageService.currentUserIsAdmin()) {
            this.canNotEdit = false;
            return;
        }
        const hasEditPermission = this.localStorageService.hasAllPermissions([10033, 10046]);
        const isErsteller = this.ticket.erstellt_von === this.currentUser.benutzer_id;
        const isTierarztOrKunde =
            this.localStorageService.isCurrentUserVeterinarian() || this.localStorageService.isCurrentUserKunde();
        this.canNotEdit = !hasEditPermission;
        if (isTierarztOrKunde) {
            this.canNotEdit = !(isErsteller && hasEditPermission);
        }
    }

    private isUserInZugewiesenAndStatusAbFreigegeben(): void {
        const inZugewiesen: boolean = !!this.ticket.zuweisungen?.find(
            z => z.benutzer_id === this.currentUser.benutzer_id,
        );
        this.userInZugewiesenAndStatusAbFreigegeben =
            inZugewiesen && this.ticket.status_id > 224 && this.ticket.status_id < 229;
    }
}
